//changed due to package upgrade
import React, { useState } from 'react';
import AppGridContainer from '@projectK/components/AppGridContainer';
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import PropTypes from 'prop-types';
import {
  useRolePermissions,
  hasPermission,
} from '@projectK/context/RolesProvider';
import IntlMessages from '@projectK/helpers/IntlMessages';

const validationSchema = yup.object({
  product_type: yup
    .string()
    .nullable()
    .max(20, <IntlMessages id='validation.productTypeTooLong' />)
    .matches(
      /^[a-zA-Z0-9\s]*$/,
      <IntlMessages id='validation.productTypeInvalidCharacters' />,
    ),
  my_locker_alias: yup
    .string()
    .max(100, <IntlMessages id='validation.myLockerAliasTooLong' />)
    .nullable(),
  waiver_name: yup
    .string()
    .max(100, <IntlMessages id='validation.waiverNameAliasTooLong' />)
    .nullable(),
  waiver_name_plural: yup
    .string()
    .max(100, <IntlMessages id='validation.waiverNamePluralAliasTooLong' />)
    .nullable(),
  account_member_alias: yup
    .string()
    .max(100, <IntlMessages id='validation.accountMemberAliasTooLong' />)
    .nullable(),
  convenience_fee_alias: yup.string().nullable(),
});
const Verbiage = ({ settings, onUpdateSettings }) => {
  const { setVerbiageSettings } = useApplicationSettings();
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();
  const { rolePermissions } = useRolePermissions();
  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        initialValues={{
          product_type: settings?.product_type || '',
          my_locker_alias: settings?.my_locker_alias || '',
          waiver_name: settings?.waiver_name || '',
          waiver_name_plural: settings?.waiver_name_plural || '',
          convenience_fee_alias: settings?.convenience_fee_alias || '',
          account_member_alias: settings?.account_member_alias || '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          // Prepare the data for submission
          const settingsArray = Object.keys(values).map((fieldName) => ({
            setting_name: 'display',
            setting_slug: fieldName,
            setting_value: values[fieldName] === '' ? null : values[fieldName],
            is_active: true, // Set is_active as needed
          }));

          // Make the PUT request using jwtAxios
          jwtAxios
            .put(`/app-settings/display/`, settingsArray)
            .then((response) => {
              setSubmitting(false);
              setIsEdit(false);
              infoViewActionsContext.showMessage(
                'Verbiage Settings updated successfully!',
              );
              setVerbiageSettings(values);
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            })
            .catch((error) => {
              // Handle the error here
              infoViewActionsContext.fetchError(error.message);
              console.error('Error updating verbiage settings:', error);
              setSubmitting(false);
            });
        }}
      >
        {({
          resetForm,
          values,
          touched,
          errors,
          setFieldValue,
          setTouched,
        }) => (
          <Form noValidate autoComplete='off'>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography
                  variant='h4'
                  sx={{
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  Verbiage Settings
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  These settings control how your products are described
                </Typography>
              </Box>
              <Box>
                {!isEdit &&
                  hasPermission(
                    rolePermissions,
                    'Company Settings - Verbiage',
                    'update',
                  ) && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsEdit(true)}
                    />
                  )}
              </Box>
            </Box>
            <Divider sx={{ my: 4 }} />
            <AppGridContainer>
              <Grid size={{ xs: 12, md: 12 }}>
                <Box
                  sx={{
                    border: '1px solid #EAECF0',
                    p: 6,
                    borderRadius: 3,
                    boxShadow:
                      '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                  }}
                >
                  <AppGridContainer>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='product_type'
                        variant='outlined'
                        value={values.product_type}
                        onChange={(event) => {
                          setFieldValue('product_type', event.target.value);
                          touched.product_type = true;
                        }}
                        error={
                          touched.product_type && Boolean(errors.product_type)
                        }
                        helperText={touched.product_type && errors.product_type}
                        sx={{
                          width: '100%',
                        }}
                        label='Product Label'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 21 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='convenience_fee_alias'
                        variant='outlined'
                        value={values.convenience_fee_alias}
                        onChange={(event) => {
                          setFieldValue(
                            'convenience_fee_alias',
                            event.target.value,
                          );
                          touched.convenience_fee_alias = true;
                        }}
                        error={
                          touched.convenience_fee_alias &&
                          Boolean(errors.convenience_fee_alias)
                        }
                        helperText={
                          touched.convenience_fee_alias &&
                          errors.convenience_fee_alias
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='Convenience Fee'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 101 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='my_locker_alias'
                        variant='outlined'
                        value={values.my_locker_alias}
                        onChange={(event) => {
                          setFieldValue('my_locker_alias', event.target.value);
                          touched.my_locker_alias = true;
                        }}
                        error={
                          touched.my_locker_alias &&
                          Boolean(errors.my_locker_alias)
                        }
                        helperText={
                          touched.my_locker_alias && errors.my_locker_alias
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='My Locker Alias'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 101 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='waiver_name'
                        variant='outlined'
                        value={values.waiver_name}
                        onChange={(event) => {
                          setFieldValue('waiver_name', event.target.value);
                          touched.waiver_name = true;
                        }}
                        error={
                          touched.waiver_name && Boolean(errors.waiver_name)
                        }
                        helperText={touched.waiver_name && errors.waiver_name}
                        sx={{
                          width: '100%',
                        }}
                        label='Waiver Name'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 101 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='waiver_name_plural'
                        variant='outlined'
                        value={values.waiver_name_plural}
                        onChange={(event) => {
                          setFieldValue(
                            'waiver_name_plural',
                            event.target.value,
                          );
                          touched.waiver_name_plural = true;
                        }}
                        error={
                          touched.waiver_name_plural &&
                          Boolean(errors.waiver_name_plural)
                        }
                        helperText={
                          touched.waiver_name_plural &&
                          errors.waiver_name_plural
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='Waiver Name Plural'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 101 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='account_member_alias'
                        variant='outlined'
                        value={values.account_member_alias}
                        onChange={(event) => {
                          setFieldValue(
                            'account_member_alias',
                            event.target.value,
                          );
                          touched.account_member_alias = true;
                        }}
                        error={
                          touched.account_member_alias &&
                          Boolean(errors.account_member_alias)
                        }
                        helperText={
                          touched.account_member_alias &&
                          errors.account_member_alias
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='Account Member Alias'
                        slotProps={{
                          input: {
                            readOnly: !isEdit,
                          },
                        }}
                        inputProps={{ maxLength: 101 }}
                      />
                    </Grid>
                  </AppGridContainer>
                </Box>
              </Grid>
            </AppGridContainer>
            {isEdit && (
              <Stack
                direction='row'
                justifyContent='flex-end'
                spacing={5}
                sx={{ mt: 3 }}
              >
                <Button
                  sx={{
                    borderColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      borderColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8)
                        : alpha('#2196f3', 0.8),
                    },
                    color: companyTheme ? companyTheme.primary.main : '#2196f3',
                  }}
                  onClick={() => {
                    setIsEdit(false);
                    resetForm();
                    setFieldValue(values);
                    setTouched({});
                  }}
                >
                  <IntlMessages id='common.cancel' />
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  type='submit'
                  sx={{
                    backgroundColor: companyTheme
                      ? companyTheme.primary.main
                      : '#2196f3',
                    '&:hover': {
                      backgroundColor: companyTheme
                        ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                        : alpha('#2196f3', 0.8),
                    },
                  }}
                >
                  <IntlMessages id='common.save' />
                </Button>
              </Stack>
            )}
          </Form>
        )}
      </Formik>
      <AppInfoView />
    </>
  );
};
export default Verbiage;
Verbiage.propTypes = {
  settings: PropTypes.object,
  onUpdateSettings: PropTypes.func,
};
